import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import { Archive, ArrowRight, Download, MapPin, PartyPopper, Truck, Search } from "lucide-react";

const downloadPdf = async (item) => {
  try {
    const res = await api.get(`/requests/${item.id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `Foglio_Servizio_${item.code?.replace("/", "_")}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("PDF non disponibile"); }
};

export default function PartnerArchivePage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get("/requests");
    setItems(data.filter((i) => i.unlocked && ["completata", "da_fatturare", "annullata"].includes(i.status))
      .sort((a, b) => String(b.operative?.data_ora_evento || "").localeCompare(String(a.operative?.data_ora_evento || ""))));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = (items || []).filter((i) => {
    if (statusFilter !== "all" && i.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [i.code, i.patient?.nominativo, i.evento?.denominazione, i.partenza?.citta, i.arrivo?.citta, i.evento?.luogo]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2">
          <Archive className="h-7 w-7 text-slate-500" /> Trasporti Archiviati
        </h1>
        <p className="text-sm text-slate-500 mb-6">Servizi completati o annullati dalla Centrale Operativa. Il foglio di servizio resta scaricabile.</p>

        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-800 mr-auto" data-testid="archive-count">{items ? `${filtered.length} servizi` : ""}</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="archive-status-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="completata">Completati</SelectItem>
                <SelectItem value="annullata">Annullati</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="archive-search" placeholder="Cerca codice, paziente, città..." value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8 w-64" />
            </div>
          </div>
          {items === null ? (
            <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-12 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Archive className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">{q ? "Nessun servizio corrisponde alla ricerca." : "Nessun servizio archiviato."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Codice</TableHead>
                  <TableHead>Servizio</TableHead>
                  <TableHead>Tratta / Luogo</TableHead>
                  <TableHead className="whitespace-nowrap">Data / Ora</TableHead>
                  <TableHead className="w-[110px]">Stato</TableHead>
                  <TableHead className="text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((i) => (
                  <TableRow key={i.id} data-testid={`archive-row-${i.id}`}>
                    <TableCell className="font-mono text-xs font-semibold text-primary whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span>{i.code}</span>
                        {i.request_type === "evento"
                          ? <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-1.5 py-0.5 text-[10px] w-fit"><PartyPopper className="h-3 w-3" /> Evento</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] w-fit"><Truck className="h-3 w-3" /> Trasporto</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-slate-700">
                      {i.request_type === "evento" ? (i.evento?.denominazione || "—") : (i.patient?.nominativo || "—")}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {i.request_type === "evento"
                        ? <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-slate-400" /> {i.evento?.luogo || "—"}</span>
                        : <span className="flex items-center gap-1.5">{i.partenza?.citta || "-"} <ArrowRight className="h-3 w-3 text-slate-400" /> {i.arrivo?.citta || "-"}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{formatDateTime(i.operative?.data_ora_evento) || "—"}</TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-8 gap-1.5" data-testid={`archive-pdf-${i.id}`} onClick={() => downloadPdf(i)}>
                        <Download className="h-4 w-4" /> Scarica
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
